import { Injectable, inject } from '@angular/core';
import {
  GraphSetpointData,
  WaterStressSetPoint,
  WaterStressSetpointBody,
} from '@cropx-common/models/DeviceGraphDataResponse';
import { SetpointFetchersService } from '@cropx-common/modules/graphs/components/fetcher/fetchers-setpoints';
import { DeviceApiService } from '@cropx-common/services/api-gateway/DeviceApiService';
import {
  Action,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';

import { GraphThresholdSetpoint } from './../../../../../../../../../libs/common/src/lib/models/DeviceGraphDataResponse';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace WaterStress {
  const type = 'WaterStress';

  export class FetchWaterStress {
    static readonly type = `[${type}] FetchWaterStress`;
    constructor(public deviceInstallationUuid: string) {}
  }

  export class PostWaterStress {
    static readonly type = `[${type}] PostWaterStress`;

    constructor(
      public deviceInstallationUuid: string,
      public body: WaterStressSetpointBody,
    ) {}
  }

  export class UpdateWaterStress {
    static readonly type = `[${type}] UpdateWaterStress`;

    constructor(
      public deviceInstallationUuid: string,
      public setpointUuid: string,
      public body: WaterStressSetpointBody,
    ) {}
  }
  export class DeleteWaterStress {
    static readonly type = `[${type}] DeleteWaterStress`;

    constructor(
      public deviceInstallationUuid: string,
      public setpointUuid: string,
    ) {}
  }
}

export interface WaterStressStateModel {
  setpoints: GraphSetpointData<WaterStressSetPoint>;
  setPointsLoading: boolean;
}

@State<WaterStressStateModel>({
  name: 'WaterStress',
  defaults: {
    setpoints: null,
    setPointsLoading: false,
  },
})
@Injectable()
export class WaterStressState {
  private api = inject(DeviceApiService);
  private setpointFetcher = inject(SetpointFetchersService);

  @Action(WaterStress.FetchWaterStress)
  fetchWaterStress(
    ctx: StateContext<WaterStressStateModel>,
    action: WaterStress.FetchWaterStress,
  ): Observable<any> {
    const { deviceInstallationUuid } = action;

    ctx.patchState({ setPointsLoading: true, setpoints: null });
    const fetcher = this.setpointFetcher.waterStressSetPoints(
      deviceInstallationUuid,
    );
    const to = dayjs().valueOf();
    const from = dayjs().subtract(1, 'year').valueOf();

    const request = fetcher.fetch(from, to);

    return request.pipe(
      tap((resp: GraphSetpointData<GraphThresholdSetpoint>) => {
        ctx.patchState({ setpoints: resp, setPointsLoading: false });
      }),
    );
  }
  @Action(WaterStress.PostWaterStress)
  postWaterStressTarget(
    ctx: StateContext<WaterStressStateModel>,
    action: WaterStress.PostWaterStress,
  ): Observable<any> {
    const { deviceInstallationUuid, body } = action;

    ctx.patchState({ setPointsLoading: true });
    return this.api.postWaterStressSetPoint(deviceInstallationUuid, body).pipe(
      tap(() => {
        ctx.dispatch(new WaterStress.FetchWaterStress(deviceInstallationUuid));
      }),
    );
  }
  @Action(WaterStress.UpdateWaterStress)
  updateWaterStress(
    ctx: StateContext<WaterStressStateModel>,
    action: WaterStress.UpdateWaterStress,
  ): Observable<any> {
    const { deviceInstallationUuid, setpointUuid, body } = action;

    return this.api
      .updateWaterStressSetPoint(deviceInstallationUuid, setpointUuid, body)
      .pipe(
        tap((resp) => {
          ctx.setState(
            produce((state) => {
              const data = state.setpoints.data.filter(
                (sp) => sp.uuid !== resp.uuid,
              );
              data.push(resp);
              data.sort((a, b) => a.timestamp - b.timestamp);
              state.setpoints.data = data;
            }),
          );
          ctx.patchState({ setPointsLoading: false });
        }),
      );
  }

  @Action(WaterStress.DeleteWaterStress)
  deleteWaterStress(
    ctx: StateContext<WaterStressStateModel>,
    action: WaterStress.DeleteWaterStress,
  ): Observable<any> {
    const { deviceInstallationUuid, setpointUuid } = action;

    return this.api
      .deleteWaterStressSetPoint(deviceInstallationUuid, setpointUuid)
      .pipe(
        tap(() => {
          ctx.setState(
            produce((state) => {
              const data = state.setpoints.data.filter(
                (sp) => sp.uuid !== setpointUuid,
              );
              data.sort((a, b) => a.timestamp - b.timestamp);
              state.setpoints.data = data;
            }),
          );

          ctx.patchState({ setPointsLoading: false });
        }),
      );
  }
}

export class WaterStressSelectors {
  static slices =
    createPropertySelectors<WaterStressStateModel>(WaterStressState);
}
